import { useMemo } from 'react';

import { State } from 'modules';

import { bindActionCreators, useDispatch, useSelector } from 'third-party';

import { DEFAULT_PAGE_SIZE } from 'constants/common';
import {
  payeeEditEndpoint,
  payeeEndpoint,
  payeeSingleEndpoint,
  payeesExport,
  userOrganizationPayeeSearch,
  userPayeeEndpoint,
} from 'constants/endpoints';

import { appSelectors } from 'selectors';

import type { PayeeExportOptions } from 'features/Payee/PayeeExportModal';

import { getPageNumberAfterLoadMore } from 'utils/helper';
import { httpMethod, simplifyBuilder } from 'utils/sra';

import {
  APIPayee,
  APIPayeeRequestModel,
  APIPayeeSearchFilter,
  APIPayeeSearchResult,
  APISortOrder,
} from 'types/api';

export type PayeeState = {
  payeeList: APIPayee[];
  payee: APIPayee | null;
  loadPayeePending: boolean;
  loadPayeesPending: boolean;
  loadUserPayeesPending: boolean;
  loadMoreUserPayeesPending: boolean;
  createPayeePending: boolean;
  updatePayeePending: boolean;
  allPayeesLoaded: boolean;
  payeeListLoaded: boolean;
  exportPayeesPending: boolean;
  filters: APIPayeeSearchFilter;
  payeeGlobalFilter: string;
  pagination: {
    pageNumber: APIPayeeSearchResult['pageNumber'];
    pageSize: APIPayeeSearchResult['pageSize'];
  };
};

export const payeeState: PayeeState = {
  payeeList: [],
  payee: null,
  loadPayeePending: false,
  exportPayeesPending: false,
  filters: {
    searchSortOrders: [
      {
        sortField: 'createdOn',
        sortOrder: APISortOrder.DESC,
      },
    ],
  },
  payeeGlobalFilter: '',
  loadPayeesPending: false,
  payeeListLoaded: false,
  loadUserPayeesPending: false,
  loadMoreUserPayeesPending: false,
  createPayeePending: false,
  updatePayeePending: false,
  allPayeesLoaded: false,
  pagination: { pageNumber: 1, pageSize: DEFAULT_PAGE_SIZE },
};

const builder = simplifyBuilder(payeeState, {});

const updatePayeeFilters = builder.createReduxAction(
  (filters: APIPayeeSearchFilter) => ({
    name: 'updatePayeeFilters',
    updater: () => ({
      filters,
    }),
  }),
);

export const loadUserPayees = builder.createServerAction(
  (organizationId: string) => ({
    name: 'loadUserPayees',
    url: userOrganizationPayeeSearch(),
    body: (state: State) => ({
      organizationIds: organizationId ? [organizationId] : undefined,
      query: state.payee.payeeGlobalFilter,
      ...state.payee.filters,
      pageNumber: 1,
      pageSize: DEFAULT_PAGE_SIZE,
    }),
    method: httpMethod.post,
    onSuccess: (state: PayeeState, payload: APIPayeeSearchResult) => ({
      payeeList: payload.result,
      payeeListLoaded: true,
      allPayeesLoaded: payload.result.length < payload.pageSize,
      pagination: {
        pageNumber: payload.pageNumber,
        pageSize: payload.pageSize,
      },
    }),
  }),
);

export const setPayeeGlobalFilter = builder.createReduxAction(
  (filter: string) => ({
    name: 'setPayeeGlobalFilter',
    updater: () => ({
      payeeGlobalFilter: filter,
    }),
  }),
);

export const loadMoreUserPayees = builder.createServerAction(
  (organizationId: string) => ({
    name: 'loadMoreUserPayees',
    url: userOrganizationPayeeSearch(),
    body: (state: State) => ({
      pageNumber: getPageNumberAfterLoadMore(
        state.payee.payeeList,
        state.payee.pagination.pageSize,
      ),
      organizationIds: organizationId ? [organizationId] : undefined,
      query: state.payee.payeeGlobalFilter,
    }),
    method: httpMethod.post,
    onSuccess: (state: PayeeState, payload: APIPayeeSearchResult) => ({
      payeeList: [...(state.payeeList || []), ...payload.result],
      payeeListLoaded: true,
      allPayeesLoaded: payload.result.length < payload.pageSize,
      pagination: {
        pageNumber: payload.pageNumber,
        pageSize: payload.pageSize,
      },
    }),
  }),
);

export const loadPayees = builder.createServerAction(
  (organizationId: string) => ({
    name: 'loadPayees',
    url: organizationId ? payeeEndpoint(organizationId) : userPayeeEndpoint(),
    body: (state: State) => ({
      organizationIds: organizationId ? [organizationId] : undefined,
      ...state.payee.filters,
      pageNumber: 1,
      pageSize: DEFAULT_PAGE_SIZE,
    }),
    method: httpMethod.post,
    onSuccess: (state: PayeeState, payload: APIPayeeSearchResult) => ({
      payeeList: payload.result,
      payeeListLoaded: true,
      allPayeesLoaded: payload.result.length < payload.pageSize,
      pagination: {
        pageNumber: payload.pageNumber,
        pageSize: payload.pageSize,
      },
    }),
  }),
);

export const loadMorePayees = builder.createServerAction(
  (organizationId: string) => ({
    name: 'loadMorePayees',
    url: organizationId ? payeeEndpoint(organizationId) : userPayeeEndpoint(),
    body: (state: State) => ({
      pageNumber: getPageNumberAfterLoadMore(
        state.payee.payeeList,
        state.payee.pagination.pageSize,
      ),
      ...state.payee.filters,
    }),
    method: httpMethod.post,
    onSuccess: (state: PayeeState, payload: APIPayeeSearchResult) => ({
      payeeList: [...(state.payeeList || []), ...payload.result],
      payeeListLoaded: true,
      allPayeesLoaded: payload.result.length < payload.pageSize,
      pagination: {
        pageNumber: payload.pageNumber,
        pageSize: payload.pageSize,
      },
    }),
  }),
);

export const loadPayee = builder.createServerAction(
  (organizationId: string, payeeId: string) => ({
    name: 'loadPayee',
    url: organizationId
      ? payeeSingleEndpoint(organizationId, payeeId)
      : payeeEditEndpoint(payeeId),
    method: httpMethod.get,
    onSuccess: (state: PayeeState, payload: APIPayee) => ({
      payee: payload,
    }),
  }),
);

export const deletePayee = builder.createServerAction(
  (organizationId: string, payeeId: string) => ({
    name: 'deletePayee',
    url: organizationId
      ? payeeSingleEndpoint(organizationId, payeeId)
      : payeeEditEndpoint(payeeId),
    method: httpMethod.delete,
    onSuccess: (state: PayeeState) => ({
      payeeList: state.payeeList.filter(payee => payee.id !== payeeId),
    }),
  }),
);

export const updatePayee = builder.createServerAction(
  (organizationId: string, payeeId: string, model: APIPayeeRequestModel) => ({
    name: 'updatePayee',
    url: payeeSingleEndpoint(organizationId, payeeId),
    method: httpMethod.post,
    body: model,
    onSuccess: (state: PayeeState, result: APIPayee) => ({
      payeeList: state.payeeList.map(payee =>
        payee.id === payeeId ? result : payee,
      ),
    }),
  }),
);

export const createPayee = builder.createServerAction(
  (organizationId: string, model: APIPayee, listOwnerId?: string) => ({
    name: 'createPayee',
    url: payeeEndpoint(organizationId),
    method: httpMethod.put,
    body: model,
    onSuccess: (state: PayeeState, result: APIPayee) => ({
      payeeList:
        listOwnerId && result.user?.id !== listOwnerId
          ? state.payeeList
          : [result, ...state.payeeList],
    }),
  }),
);

const resetPayeeFilters = builder.createReduxAction(() => ({
  name: 'resetPayeeFilters',
  updater: () => ({
    filters: payeeState.filters,
    pagination: payeeState.pagination,
  }),
}));

export const clearPayee = builder.createReduxAction(() => ({
  name: 'clearPayee',
  updater: () => ({
    payee: null,
  }),
}));

export const clearPayeeList = builder.createReduxAction(() => ({
  name: 'clearPayeeList',
  updater: () => ({
    payeeList: [],
    payeeListLoaded: false,
  }),
}));

const exportPayees = builder.createServerAction(
  (organizationId: string, options: PayeeExportOptions) => ({
    name: 'exportPayees',
    url: payeesExport(organizationId),
    body: (state: State) => ({
      ...state.payee.filters,
      format: options.format,
      columns: options.columns,
      exportPageSize: options.exportPageSize,
    }),
    method: httpMethod.post,
    responseReader: (response: any) => response.blob(),
  }),
);

export const usePayee = () => {
  const dispatch = useDispatch();
  const organizationId = useSelector(appSelectors.organizationId);

  return useMemo(
    () =>
      bindActionCreators(
        {
          updatePayeeFilters,
          resetPayeeFilters,
          clearPayee,
          setPayeeGlobalFilter,
          updatePayee: updatePayee,
          createPayee: createPayee,
          clearPayeeList: clearPayeeList,
          loadUserPayees: loadUserPayees.bind(null, organizationId),
          loadMoreUserPayees: loadMoreUserPayees.bind(null, organizationId),
          exportPayees: exportPayees.bind(null, organizationId),
          loadPayee: loadPayee.bind(null, organizationId),
          deletePayee: deletePayee.bind(null, organizationId),
          loadPayees: loadPayees.bind(null, organizationId),
          loadMorePayees: loadMorePayees.bind(null, organizationId),
        },
        dispatch,
      ),
    [dispatch, organizationId],
  );
};

export const payeeReducer = builder.getReducers();
